import React, { useState } from 'react';
import { ArrowLeft, Download, FileText, Eye, BarChart3, Lightbulb, AlertTriangle, Target, TrendingUp, RefreshCw, Share2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Progress } from './ui/progress';
import { MetricsDashboard } from './MetricsDashboard';
import { UnifiedPriorityList } from './UnifiedPriorityList';
import { ExecutiveSummary } from './ExecutiveSummary';
import { SlideCarousel } from './SlideCarousel';
import DesignAnalysisTool from './DesignAnalysisTool';
import SlideAnalysis from './SlideAnalysis';
import PresentationInsightsCharts from './PresentationInsightsCharts';
import ReAnalyzeModal from './ReAnalyzeModal';
import { OverviewTab } from './OverviewTab';
import { SlideBySlideTab } from './SlideBySlideTab';
import ShareReportModal from './ShareReportModal';

export function AnalysisWorkspace({ project, analysis, onBack, onAnalysisComplete }) {
  const [viewMode, setViewMode] = useState('analysis'); // 'analysis' or 'demo'
  const [showReAnalyzeModal, setShowReAnalyzeModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'slide-by-slide'
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const openAiKey = process.env.REACT_APP_OPENAI_API_KEY || '';
  const currentAnalysisFocus = project?.uploadMetadata?.audienceInfo?.analysisFocus || 'both';
  const currentExpertiseLevel = project?.uploadMetadata?.audienceInfo?.expertiseLevel || 'beginner';

  if (!project) {
    return null;
  }

  const handleAnalysisComplete = (payload) => {
    if (typeof onAnalysisComplete === 'function') {
      onAnalysisComplete(payload);
    }
  };

  const handleReAnalyze = async ({ analysisFocus, expertiseLevel }) => {
    // Re-analyze with new settings
    // This will trigger the analysis tool with updated audience info
    const updatedAudienceInfo = {
      ...project?.uploadMetadata?.audienceInfo,
      analysisFocus,
      expertiseLevel,
    };

    // Clear the current analysis first
    if (typeof onAnalysisComplete === 'function') {
      // Signal to clear analysis and prepare for re-analysis
      onAnalysisComplete({
        reAnalyze: true,
        audienceInfo: updatedAudienceInfo,
        file: project.uploadedFile,
      });
    }

    // Force component to re-render by updating state
    // This will cause the "no analysis" view to show the upload tool with the existing file
    setShowReAnalyzeModal(false);
  };

  // If no analysis yet, show the upload/analysis tool OR demo mode
  if (!analysis) {
    return (
      <div className="min-h-screen bg-background">
        <div className="sticky top-0 z-10 border-b border-border/40 bg-card/60 backdrop-blur">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={onBack}
                className="h-10 rounded-lg border border-border/40 bg-background/40 px-3 text-sm text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Projects
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-foreground">{project.name}</h1>
                <p className="text-sm text-muted-foreground">{project.client || 'Client TBD'}</p>
              </div>
            </div>
            <Button
              variant={viewMode === 'demo' ? 'default' : 'outline'}
              onClick={() => setViewMode(viewMode === 'demo' ? 'analysis' : 'demo')}
              className="flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              {viewMode === 'demo' ? 'Exit Demo' : 'View Demo'}
            </Button>
          </div>
        </div>

        {viewMode === 'demo' ? (
          <div className="p-6">
            <SlideAnalysis
              analysis={null}
              apiKey={null}
              uploadedFileName={null}
              initialDemoMode={true}
              projectId={project?.id}
            />
          </div>
        ) : (
          <DesignAnalysisTool
            project={project}
            hideHeader
            initialFile={project?.uploadedFile}
            onAnalysisComplete={handleAnalysisComplete}
          />
        )}
      </div>
    );
  }

  // Extract data from analysis
  const slides = analysis?.slides || [];
  const presentationType = analysis?.presentationType?.primary || 'Unknown';

  // Calculate scores based on slide recommendations if presentationScore doesn't exist
  const calculateScoreFromSlides = () => {
    if (!slides || slides.length === 0) return 0;

    // Calculate average issues per slide and create a quality score
    let totalIssues = 0;
    let slidesWithRecs = 0;

    slides.forEach(slide => {
      const recs = slide.recommendations;
      if (recs) {
        const contentRefinements = recs.content_refinements?.length || 0;
        const visualContent = recs.visual_content?.length || 0;
        totalIssues += contentRefinements + visualContent;
        slidesWithRecs++;
      }
    });

    if (slidesWithRecs === 0) return 0;

    // Calculate average issues per slide
    const avgIssuesPerSlide = totalIssues / slidesWithRecs;

    // Score calculation:
    // 0-2 issues = 100-90 score (excellent)
    // 3-4 issues = 89-75 score (good)
    // 5-6 issues = 74-60 score (fair)
    // 7+ issues = 59-40 score (needs work)
    let score;
    if (avgIssuesPerSlide <= 2) {
      score = 100 - (avgIssuesPerSlide * 5);
    } else if (avgIssuesPerSlide <= 4) {
      score = 89 - ((avgIssuesPerSlide - 2) * 7);
    } else if (avgIssuesPerSlide <= 6) {
      score = 74 - ((avgIssuesPerSlide - 4) * 7);
    } else {
      score = Math.max(40, 59 - ((avgIssuesPerSlide - 6) * 3));
    }

    const calculatedScore = Math.round(score);
    console.log('[AnalysisWorkspace] Avg issues per slide:', avgIssuesPerSlide.toFixed(1), 'Score:', calculatedScore);
    return calculatedScore;
  };

  const overallScore = analysis?.presentationScore?.overall || analysis?.overallScore || calculateScoreFromSlides();
  console.log('[AnalysisWorkspace] Overall score:', overallScore, 'presentationScore:', analysis?.presentationScore);

  // Calculate additional metrics based on slide data
  const calculateClarityIndex = () => {
    if (!slides || slides.length === 0) return 70;

    // Base clarity on content refinements - fewer refinements = higher clarity
    const avgRefinements = slides.reduce((sum, slide) => {
      const recs = slide.recommendations;
      const contentIssues = recs?.content_refinements?.length || 0;
      return sum + contentIssues;
    }, 0) / slides.length;

    // Convert to percentage (fewer issues = higher clarity)
    return Math.max(20, Math.min(100, Math.round(100 - (avgRefinements * 10))));
  };

  const calculateEngagement = () => {
    if (!slides || slides.length === 0) return 75;

    // Base engagement on visual content quality
    const slidesWithVisuals = slides.filter(slide => {
      const recs = slide.recommendations;
      return recs?.visual_content && recs.visual_content.length > 0;
    }).length;

    return Math.round((slidesWithVisuals / slides.length) * 100);
  };

  const calculateAvgViewTime = () => {
    if (!slides || slides.length === 0) return '—';

    // Estimate based on slide count and content density
    const avgWordsPerSlide = slides.reduce((sum, slide) => {
      const words = (slide.text_content || '').split(/\s+/).length;
      return sum + words;
    }, 0) / slides.length;

    // Rough estimate: 150 words per minute reading speed
    const minutesPerSlide = avgWordsPerSlide / 150;
    const totalMinutes = minutesPerSlide * slides.length;

    if (totalMinutes < 1) return `${Math.round(totalMinutes * 60)}s`;
    return `${totalMinutes.toFixed(1)}m`;
  };
  const strategicAnalysis = {
    designDirection: analysis?.strategicDirection?.primaryStrategy || analysis?.strategicDirection?.overview || '',
    keyStrengths: analysis?.strengthsAndOpportunities?.strengths || analysis?.executionGuidance?.quickWins || [],
    improvementAreas: analysis?.strengthsAndOpportunities?.opportunities || analysis?.executionGuidance?.priorityFixes || []
  };
  const executionGuidance = analysis?.executionGuidance || {};

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'medium':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Left Sidebar */}
      <div className="w-80 bg-sidebar border-r border-sidebar-border flex flex-col">
        <div className="p-6 border-b border-sidebar-border">
          <Button variant="ghost" onClick={onBack} className="mb-4 p-0 text-sidebar-foreground hover:bg-sidebar-accent">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Projects
          </Button>
          <h2 className="text-xl font-semibold text-sidebar-foreground">{project.name}</h2>
          <p className="text-sm text-sidebar-foreground/80">{project.client || 'Client TBD'}</p>
        </div>

        <div className="p-6 border-b border-sidebar-border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-sidebar-foreground">Analysis Details</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowReAnalyzeModal(true)}
              className="h-8 text-xs"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Re-Analyze
            </Button>
          </div>
          <div className="space-y-3 text-sm">
            <div>
              <label className="font-medium text-sidebar-foreground">Presentation Type:</label>
              <p className="text-sidebar-foreground/80 mt-1">{presentationType}</p>
            </div>
            <div>
              <label className="font-medium text-sidebar-foreground">Slides:</label>
              <p className="text-sidebar-foreground/80 mt-1">{slides.length} slides analyzed</p>
            </div>
            <div>
              <label className="font-medium text-sidebar-foreground">Overall Score:</label>
              <p className="text-sidebar-foreground/80 mt-1 font-semibold">{overallScore}/100</p>
            </div>
          </div>
        </div>


        <div className="p-6 border-t border-sidebar-border">
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShareModalOpen(true)}
              className="w-full border-sidebar-border text-sidebar-foreground hover:bg-sidebar-accent"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
            <Button variant="outline" size="sm" className="w-full border-sidebar-border text-sidebar-foreground hover:bg-sidebar-accent">
              <FileText className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button variant="outline" size="sm" className="w-full border-sidebar-border text-sidebar-foreground hover:bg-sidebar-accent">
              <Download className="w-4 h-4 mr-2" />
              Report
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content - Tabbed View */}
      <div className="flex-1 flex flex-col">
        {/* Tab Navigation */}
        <div className="border-b border-sidebar-border bg-card/60 backdrop-blur">
          <div className="flex gap-1 px-6 pt-4">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 font-medium text-sm rounded-t-lg transition-all ${
                activeTab === 'overview'
                  ? 'bg-background text-foreground border-b-2 border-accent'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Overview
              </div>
            </button>
            <button
              onClick={() => setActiveTab('slide-by-slide')}
              className={`px-6 py-3 font-medium text-sm rounded-t-lg transition-all ${
                activeTab === 'slide-by-slide'
                  ? 'bg-background text-foreground border-b-2 border-accent'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Slide-by-Slide
              </div>
            </button>
          </div>
        </div>

        {/* Content Area */}
        <ScrollArea className="flex-1 p-6">
          <div className="space-y-8 max-w-[1800px] mx-auto">
            {activeTab === 'overview' && (
              <OverviewTab
                analysis={analysis}
                analysisFocus={currentAnalysisFocus}
                expertiseLevel={currentExpertiseLevel}
                projectId={project?.id}
              />
            )}

            {activeTab === 'slide-by-slide' && (
              <SlideBySlideTab
                slides={analysis?.slides || []}
                analysis={analysis}
                expertiseLevel={currentExpertiseLevel}
                analysisFocus={currentAnalysisFocus}
              />
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Re-Analyze Modal */}
      <ReAnalyzeModal
        isOpen={showReAnalyzeModal}
        onClose={() => setShowReAnalyzeModal(false)}
        onReAnalyze={handleReAnalyze}
        currentFocus={currentAnalysisFocus}
        currentExpertiseLevel={currentExpertiseLevel}
      />

      {/* Share Report Modal */}
      <ShareReportModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        analysisData={analysis}
        presentationName={project?.name || 'Untitled Presentation'}
      />
    </div>
  );
}

export default AnalysisWorkspace;
